import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  CoverageResult,
  StudentLoanAssistanceData,
  StudentLoanCalculationParameters,
  StudentLoanResult,
} from 'api/StudentLoanApi';
import {
  getApplicationData,
  getLoanOffer,
  uploadStudentAidData,
  getStudentLoanAssistanceData,
  updateStudentLoanAssistanceData,
} from 'thunks';

import { GetApplicationDataResponse } from './applicationData';
import { LoanOfferResponse } from './loanOffer';

interface StudentLoanDataFetchStatus {
  isLoading: boolean;
  error: boolean;
  fetched: boolean;
  refetch: boolean;
}

export interface StudentLoanData {
  applicationId?: string;
  result?: StudentLoanResult;
  hospital?: CoverageResult;
  /** Questionnaire answers from this session and/or hydrated from assistanceData. */
  answers: StudentLoanCalculationParameters;
}

export type StudentLoanDataState = StudentLoanData & StudentLoanDataFetchStatus;

const initialState: StudentLoanDataState = {
  isLoading: false,
  error: false,
  fetched: false,
  refetch: false,
  answers: {},
};

const updateState = (state: StudentLoanDataState, { payload }: PayloadAction<StudentLoanAssistanceData>) => {
  state.isLoading = false;
  state.error = false;
  state.fetched = true;
  state.result = payload.result;
  state.hospital = payload.hospital;
  if (payload.answers) {
    state.answers = { ...state.answers, ...payload.answers };
  }
};

const studentLoanData = createSlice({
  name: 'studentLoanData',
  initialState,
  reducers: {
    setStudentLoanApplicationId: (state: StudentLoanDataState, { payload }: PayloadAction<string>) => {
      state.applicationId = payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getStudentLoanAssistanceData.pending, (state, action) => {
      state.applicationId = action.meta.arg.applicationId;
      state.refetch = state.fetched;
      state.isLoading = true;
      state.fetched = false;
    });
    builder.addCase(getStudentLoanAssistanceData.fulfilled, updateState);
    builder.addCase(getStudentLoanAssistanceData.rejected, (state) => {
      state.isLoading = false;
      state.error = true;
      state.fetched = false;
    });
    builder.addCase(updateStudentLoanAssistanceData.pending, (state, action) => {
      const { applicationId, ...answers } = action.meta.arg;
      state.applicationId = applicationId;
      state.answers = { ...state.answers, ...answers };
      state.refetch = state.fetched;
      state.isLoading = true;
      state.fetched = false;
    });
    builder.addCase(updateStudentLoanAssistanceData.fulfilled, updateState);
    builder.addCase(updateStudentLoanAssistanceData.rejected, (state, action) => {
      // The pending reducer already merged these answers. Drop that attempt so routing
      // does not treat an unsaved answer as stored. The next GET restores the saved row.
      const { applicationId, ...attempted } = action.meta.arg;
      state.applicationId = applicationId;
      (Object.keys(attempted) as (keyof StudentLoanCalculationParameters)[]).forEach((key) => {
        delete state.answers[key];
      });
      state.isLoading = false;
      state.fetched = state.refetch;
    });
    builder.addCase(getApplicationData.fulfilled, (state, { payload }: PayloadAction<GetApplicationDataResponse>) => {
      state.applicationId = payload.application.id;
    });
    builder.addCase(getLoanOffer.fulfilled, (state, { payload }: PayloadAction<LoanOfferResponse>) => {
      if (payload.data.application_id) {
        state.applicationId = payload.data.application_id;
      }
    });
    builder.addCase(uploadStudentAidData.fulfilled, (state, { payload }) => {
      state.result = payload.result;
      state.hospital = payload.hospital;
      if (payload.answers) {
        state.answers = { ...state.answers, ...payload.answers };
      }
    });
  },
});

export const { setStudentLoanApplicationId } = studentLoanData.actions;

export default studentLoanData.reducer;
